/**
 * PublicCmdKPalette — Iter258.7
 *
 * Cmd/Ctrl+K command palette for the PUBLIC qortrace.com site.
 *
 * Searches:
 *   - Curated exposed wallets  (via /api/public/cmdk/search?q=…)
 *   - Delivered audits         (same endpoint)
 *   - Static top-level pages   (client-side list)
 *
 * Mounted globally inside Header.jsx so any visitor can press Cmd/Ctrl+K
 * (or "/") anywhere on the public site and jump to a wallet / audit / page.
 *
 * Why it matters: lets press + investors find a specific wallet
 * ("Bitfinex", "Mt Gox") on the leaderboard in one keystroke instead of
 * scrolling 20 rows on a phone.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Loader2, Search, X,
  Wallet, ShieldCheck, Compass, ScanLine, BookOpen, Newspaper,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter269.1 — self-tuning relevance. Each result open fires a
// fire-and-forget POST so the backend can rank popular destinations
// higher on future searches. Errors are swallowed: we never want
// click-tracking to block navigation.
function trackCmdkClick({ q, to, kind, label }) {
  try {
    axios.post(`${API}/api/public/cmdk/click`, {
      q: q || "", to, kind, label: label || "",
    }, { timeout: 4000 }).catch(() => {});
  } catch { /* never throw */ }
}

// Crypto-address detection — broad on purpose. We only need to know
// "does this LOOK like an address" so the synthetic CTA can offer to
// scan it. The downstream BulkScan does proper chain-aware validation.
const ADDR_PATTERNS = [
  { name: "ethereum", re: /^0x[0-9a-fA-F]{40}$/ },
  { name: "bitcoin",  re: /^(bc1|tb1)[02-9ac-hj-np-z]{6,87}$/i },
  { name: "bitcoin",  re: /^[13][1-9A-HJ-NP-Za-km-z]{25,40}$/ },
  { name: "tron",     re: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ },
  { name: "solana",   re: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/ },
];

function detectAddress(raw) {
  const v = (raw || "").trim();
  if (v.length < 25 || v.length > 95) return null;
  for (const { name, re } of ADDR_PATTERNS) {
    if (re.test(v)) return { chain: name, address: v };
  }
  return null;
}

// Static public-site pages indexed in the palette. We keep the list
// short (10 entries) — palette UX degrades fast past ~15 items.
const PUBLIC_PAGES = [
  { to: "/threat-radar/exposed-wallets", label: "Exposed Wallets Leaderboard", icon: Compass, hint: "Threat Radar" },
  { to: "/threat-radar",                  label: "Threat Radar",                icon: Compass, hint: "Threat Radar" },
  { to: "/pricing",                       label: "Pricing",                     icon: Compass, hint: "Self-Serve" },
  { to: "/audits",                        label: "Audit Marketplace",          icon: ShieldCheck, hint: "Self-Serve" },
  { to: "/enterprise",                    label: "Enterprise",                 icon: Compass, hint: "Enterprise" },
  { to: "/status",                        label: "Trust Center / Status",      icon: Compass, hint: "Trust" },
  { to: "/contact",                       label: "Contact",                    icon: Compass, hint: "" },
  { to: "/account",                       label: "My account",                 icon: Compass, hint: "" },
];

const KIND_HEADERS = {
  trending: "TRENDING TODAY",
  scan:   "SCAN THIS WALLET",
  page:   "PAGES",
  wallet: "EXPOSED WALLETS",
  audit:  "DELIVERED AUDITS",
  doc:    "DOCS",
  news:   "NEWS",
};

// Iter269.2 — icon picker for trending rows so each kind reads at a
// glance. Mirrors the per-kind icons used elsewhere in the palette.
const TRENDING_ICON = {
  page: Compass, wallet: Wallet, audit: ShieldCheck,
  doc: BookOpen, news: Newspaper,
};


function fuzzyScore(label, q) {
  if (!q) return 1;
  const a = label.toLowerCase();
  const needle = q.toLowerCase();
  if (a.startsWith(needle))     return 3;
  if (a.includes(` ${needle}`)) return 2;
  if (a.includes(needle))       return 1;
  let i = 0;
  for (const c of a) {
    if (c === needle[i]) i++;
    if (i === needle.length) return 0.5;
  }
  return 0;
}


export default function PublicCmdKPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [backend, setBackend] = useState({
    pages: [], wallets: [], audits: [], docs: [], news: [],
  });
  // Iter269.2 — "Trending today" rail shown when the query is empty.
  // We refresh on every palette open so popularity matches "right now".
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setBackend({ pages: [], wallets: [], audits: [], docs: [], news: [] });
      setTimeout(() => inputRef.current?.focus(), 30);
      // Kick off a trending fetch so the empty palette has real
      // "what other people are clicking" content instead of a frozen
      // 5-item PUBLIC_PAGES stub.
      (async () => {
        try {
          const r = await axios.get(`${API}/api/public/cmdk/click-stats`, {
            params: {
              limit:       3,
              since_hours: 24,
              kinds:       "page,doc,wallet,audit",
            },
            timeout: 4000,
          });
          setTrending(r.data?.items || []);
        } catch {
          setTrending([]);
        }
      })();
    } else {
      setQ("");
      setActiveIdx(0);
    }
  }, [open]);

  // Esc closer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Static pages fuzzy-filtered locally. Used as a graceful fallback
  // when the user hasn't typed enough for the backend hit yet, or when
  // the backend is slow. Once `backend.pages` arrives we prefer that
  // (which is the full registry from /app/backend/public_site_index.py
  // and is the source the click-boost feature also ranks against, so
  // popularity bumps the user sees here stay consistent).
  const pageHits = useMemo(() => {
    const term = q.trim();
    if (!term) return PUBLIC_PAGES.slice(0, 5); // show top 5 popular on empty
    if ((backend.pages || []).length > 0) {
      return backend.pages.map((p) => ({
        to: p.to, label: p.label,
        sublabel: p.sublabel || "", icon: Compass,
      }));
    }
    return PUBLIC_PAGES
      .map((p) => ({ ...p, _score: fuzzyScore(p.label, term) }))
      .filter((p) => p._score > 0)
      .sort((a, b) => b._score - a._score);
  }, [q, backend.pages]);

  // Debounced backend search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setBackend({ pages: [], wallets: [], audits: [], docs: [], news: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/public/cmdk/search`, {
          params: { q: term },
        });
        setBackend(r.data?.results_by_kind || {
          pages: [], wallets: [], audits: [], docs: [], news: [],
        });
      } catch {
        setBackend({ pages: [], wallets: [], audits: [], docs: [], news: [] });
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [open, q]);

  // Merge into one keyboard-navigable list. Order: scan-cta (if address
  // detected) → pages → wallets → audits.
  const visibleItems = useMemo(() => {
    const items = [];

    // Iter258.8 — synthetic "Scan this wallet" CTA at the top when the
    // user pastes / types a raw crypto address. Converts cold curiosity
    // into a real audit lead.
    const detected = detectAddress(q);
    if (detected) {
      const short = `${detected.address.slice(0, 10)}…${detected.address.slice(-6)}`;
      items.push({
        to: `/scan/bulk?prefill=${encodeURIComponent(detected.address)}`,
        label: `Scan ${short}`,
        sublabel: `Run a free HNDL exposure check on this ${detected.chain.toUpperCase()} address`,
        icon: ScanLine,
        testid: `scan-${detected.chain}`,
        _kind: "scan",
      });
    }

    const pages = pageHits.map((p) => ({ ...p, _kind: "page" }));
    const wallets = (backend.wallets || []).map((w, i) => ({
      to: w.to,
      label: w.label,
      sublabel: w.sublabel,
      icon: Wallet,
      testid: `wallet-${i}`,
      _kind: "wallet",
    }));
    const audits = (backend.audits || []).map((a, i) => ({
      to: a.to,
      label: a.label,
      sublabel: a.sublabel,
      icon: ShieldCheck,
      testid: `audit-${i}`,
      _kind: "audit",
    }));

    // Iter269.2 — "Trending today" rail only shown on the empty
    // palette (no query yet). Surfaces real popularity instead of a
    // frozen suggestion list, and gives newcomers instant social proof
    // that the search is alive. Hidden as soon as the user starts
    // typing — at that point the natural ranker takes over and the
    // header would be visual noise.
    if (!q.trim() && (trending || []).length > 0) {
      const trendingItems = trending.slice(0, 3).map((t, i) => ({
        to:       t.to,
        // We trust the backend's `last_label` first (most recent click
        // observed it), fall back to a slug-derived title so the row
        // never reads as "(blank)" for legacy data.
        label:    t.last_label ||
                  (t.to || "").replace(/^\//, "").split("/").pop()
                                .replace(/-/g, " ") || t.to,
        sublabel: t.sublabel || `${t.count} ${t.count === 1 ? "click" : "clicks"} · last 24h`,
        icon:     TRENDING_ICON[t.kind] || Compass,
        testid:   `trending-${i}`,
        _kind:    "trending",
        // Preserve the original kind so click-tracking attributes the
        // boost to the right collection on the next click.
        _trackingKind: t.kind || "page",
      }));
      return [...items, ...trendingItems, ...pages, ...wallets, ...audits];
    }

    return [...items, ...pages, ...wallets, ...audits];
  }, [q, pageHits, backend, trending]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, visibleItems.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const hit = visibleItems[activeIdx];
        if (hit) {
          trackCmdkClick({
            q, to: hit.to,
            kind: hit._trackingKind || hit._kind,
            label: hit.label,
          });
          if (hit.external) {
            window.open(hit.to, "_blank", "noopener,noreferrer");
          } else {
            navigate(hit.to);
          }
          onClose?.();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, visibleItems, activeIdx, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="public-cmdk-palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(4,9,20,0.78)",
        backdropFilter: "blur(6px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        paddingTop: "12vh",
      }}
    >
      <div
        style={{
          width: "min(640px, 92vw)",
          background: "#0b1224",
          border: "1px solid var(--qt-border)",
          borderRadius: 8,
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          overflow: "hidden",
        }}
      >
        {/* Input row */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px",
            borderBottom: "1px solid var(--qt-border)",
          }}
        >
          <Search size={14} style={{ color: "var(--qt-text-3)" }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
            placeholder='Try "Bitfinex", "Mt Gox", "vitalik", "Pricing"…'
            data-testid="public-cmdk-input"
            style={{
              flex: 1, background: "transparent",
              border: 0, outline: 0,
              color: "#fff", fontSize: 14,
            }}
          />
          {loading && (
            <Loader2 size={12} className="animate-spin"
                     style={{ color: "var(--qt-text-3)" }} />
          )}
          <span
            className="qt-mono"
            style={{
              fontSize: 10, letterSpacing: "0.12em",
              color: "var(--qt-text-3)",
              border: "1px solid var(--qt-border)",
              borderRadius: 4, padding: "2px 6px",
            }}
          >ESC</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="public-cmdk-close"
            style={{
              background: "transparent", border: 0, padding: 4, cursor: "pointer",
              color: "var(--qt-text-3)",
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Results list */}
        <div
          data-testid="public-cmdk-results"
          style={{
            maxHeight: "55vh",
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          {q.trim() && visibleItems.length === 0 && (
            <div
              data-testid="public-cmdk-empty"
              style={{
                padding: "24px 14px",
                color: "var(--qt-text-3)",
                fontSize: 13, textAlign: "center",
              }}
            >
              {loading
                ? "Searching…"
                : <>No pages, wallets, or audits match &ldquo;{q}&rdquo;.</>}
            </div>
          )}
          {visibleItems.map((item, idx) => {
            const Icon = item.icon;
            const active = idx === activeIdx;
            const prevKind = idx > 0 ? visibleItems[idx - 1]._kind : null;
            const header = (item._kind !== prevKind) ? KIND_HEADERS[item._kind] : null;
            return (
              <React.Fragment key={`${item._kind}-${item.to}-${idx}`}>
                {header && (
                  <div
                    className="qt-mono"
                    style={{
                      padding: "10px 14px 4px",
                      color: "var(--qt-text-3)",
                      fontSize: 10, letterSpacing: "0.14em",
                    }}
                  >
                    {header}
                  </div>
                )}
                <button
                  onClick={() => {
                    trackCmdkClick({
                      q, to: item.to,
                      kind: item._trackingKind || item._kind,
                      label: item.label,
                    });
                    if (item.external) {
                      window.open(item.to, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(item.to);
                    }
                    onClose?.();
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                  data-testid={`public-cmdk-item-${item.testid || item.to.replace(/\W+/g, "-")}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%",
                    padding: "10px 14px",
                    background: active ? "rgba(0,229,255,0.10)" : "transparent",
                    border: 0,
                    borderLeft: `2px solid ${active ? "var(--qt-cyan)" : "transparent"}`,
                    color: active ? "#fff" : "var(--qt-text-2)",
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {Icon ? <Icon size={14} style={{ color: active ? "var(--qt-cyan)" : "var(--qt-text-3)" }} /> : null}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: "block", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {item.label}
                    </span>
                    {item.sublabel && (
                      <span className="qt-mono"
                            style={{
                              display: "block", fontSize: 10,
                              color: "var(--qt-text-3)",
                              letterSpacing: "0.04em",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                        {item.sublabel}
                      </span>
                    )}
                  </span>
                  {active ? <ArrowRight size={12} /> : null}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: "8px 14px",
            borderTop: "1px solid var(--qt-border)",
            display: "flex", alignItems: "center", gap: 14,
            color: "var(--qt-text-3)",
            fontSize: 10, letterSpacing: "0.12em",
          }}
          className="qt-mono"
        >
          <span>↑↓ NAVIGATE</span>
          <span>↵ JUMP</span>
          <span>ESC CLOSE</span>
          <span style={{ marginLeft: "auto" }}>
            {loading ? "SEARCHING…" : `${visibleItems.length} RESULTS`}
          </span>
        </div>
      </div>
    </div>
  );
}
